import type { CloudEnvironmentStorage } from '../types/CloudEnvironmentStorage';
import type { CloudEnvironment } from '../types/CloudEnvironment';
import {
    authoritativeGetItem,
    getItem,
    localStorageExists,
    type LocalStorageKeys,
    setItem,
} from 'owa-local-storage';

/**
 * Local storage implementation of CloudEnvironmentStorage
 */
export class LocalStorageCloudEnvironmentStorage implements CloudEnvironmentStorage {
    async getConfigForDomain(domain: string): Promise<CloudEnvironment | undefined> {
        // First try lookup using the cache from the in-context local storage, which may
        // be the actual local storage or a worker's (possibly stale) in-memory snapshot of it.
        const inContextCache = getInContextCloudEnvironmentCache();
        const cloudEnv = lookupConfigInCache(domain, inContextCache);
        if (cloudEnv) {
            return cloudEnv;
        }

        // If not found in the in-context cache and local storage is not available in our context,
        // we use the authoritative getter, which will get the item from the actual local storage
        if (!localStorageExists(self)) {
            const authoritativeCache = await getAuthoritativeCloudEnvironmentCache();
            return lookupConfigInCache(domain, authoritativeCache);
        }

        return undefined;
    }

    async putConfigForDomain(domain: string, cloudEnv: CloudEnvironment): Promise<void> {
        // We always start from the authoritative cache object to ensure we don't write
        // stale data (e.g. from the worker's in-memory snapshot) into local storage
        const cloudEnvironmentCache = await getAuthoritativeCloudEnvironmentCache();

        // Update the domain config cache object
        cloudEnvironmentCache.domainConfig[domain] = makeDomainConfigEntry(cloudEnv);

        // Update the environment config cache object
        cloudEnvironmentCache.environmentConfig[cloudEnv.environmentKey] =
            makeEnvironmentConfigEntry(cloudEnv);

        // Persist updated caches in local storage
        setCloudEnvironmentCache(cloudEnvironmentCache);
    }

    async getKnownDomains(): Promise<string[]> {
        const cloudEnvironmentCache = await getAuthoritativeCloudEnvironmentCache();
        return Object.keys(cloudEnvironmentCache.domainConfig);
    }

    async exportConfigs(): Promise<string> {
        const cloudEnvironmentCache = await getAuthoritativeCloudEnvironmentCache();
        return JSON.stringify(cloudEnvironmentCache);
    }

    importConfigs(configsPayload: string): void {
        const parsedConfigs = JSON.parse(configsPayload);

        if (!isCloudEnvironmentCache(parsedConfigs)) {
            throw new Error('Invalid CloudEnvironmentCache data');
        }

        setCloudEnvironmentCache(parsedConfigs);
    }
}

export function makeDomainConfigEntry(cloudEnv: CloudEnvironment): DomainConfig {
    return {
        environmentKey: cloudEnv.environmentKey,
        tenantId: cloudEnv.tenantId,
    };
}

export function makeEnvironmentConfigEntry(cloudEnv: CloudEnvironment): EnvironmentConfig {
    return {
        msaAuthenticationEndpoint: cloudEnv.msaAuthenticationEndpoint,
        orgAuthenticationEndpoint: cloudEnv.orgAuthenticationEndpoint,
        outlookWebOrigin: cloudEnv.outlookWebOrigin,
        usesGlobalDirectory: cloudEnv.usesGlobalDirectory,
    };
}

function isCloudEnvironmentCache(data: any): data is CloudEnvironmentCache {
    return (
        isObject(data) &&
        isDomainConfigCache(data.domainConfig) &&
        isEnvironmentConfigCache(data.environmentConfig)
    );
}

function isDomainConfigCache(data: any): data is DomainConfigCache {
    if (!isObject(data)) {
        return false;
    }

    for (const key of Object.keys(data)) {
        const domainConfig = data[key];
        if (
            !isObject(domainConfig) ||
            !isString(domainConfig.environmentKey) ||
            !isOptionalString(domainConfig.tenantId)
        ) {
            return false;
        }
    }

    return true;
}

function isEnvironmentConfigCache(data: any): data is EnvironmentConfigCache {
    if (!isObject(data)) {
        return false;
    }

    for (const key of Object.keys(data)) {
        const environmentConfig = data[key];
        if (
            !isObject(environmentConfig) ||
            !isOptionalString(environmentConfig.msaAuthenticationEndpoint) ||
            !isString(environmentConfig.orgAuthenticationEndpoint) ||
            !isString(environmentConfig.outlookWebOrigin) ||
            typeof environmentConfig.usesGlobalDirectory !== 'boolean'
        ) {
            return false;
        }
    }

    return true;
}

function isObject(data: any): boolean {
    return typeof data === 'object' && data !== null;
}

function isOptionalString(value: any): value is string | undefined {
    return value === undefined || typeof value === 'string';
}

function isString(value: any): value is string {
    return typeof value === 'string';
}

/**
 * We maintain a cache of 'domain'->'environment key' -- we do this instead
 * of 'domain'->'config' to avoid duplication of config details for multiple
 * domains in the same environment (to reduce storage footprint)
 */
type DomainConfigCache = Record<string, DomainConfig>;
type DomainConfig = {
    readonly environmentKey: string; // Opaque key to identify the environment
    readonly tenantId?: string;
};

/**
 * We maintain a cache of 'environment key'->'config'
 */
type EnvironmentConfigCache = Record<string, EnvironmentConfig>;
type EnvironmentConfig = {
    readonly msaAuthenticationEndpoint?: string;
    readonly orgAuthenticationEndpoint: string;
    readonly outlookWebOrigin: string;
    readonly usesGlobalDirectory: boolean;
};

/**
 * We store both of the above caches in local storage within the same object
 */
export type CloudEnvironmentCache = {
    readonly domainConfig: DomainConfigCache;
    readonly environmentConfig: EnvironmentConfigCache;
};

export function lookupConfigInCache(
    domain: string,
    cloudEnvironmentCache: CloudEnvironmentCache
): CloudEnvironment | undefined {
    // Look up the environment name and tenant ID for the domain
    const domainConfig = cloudEnvironmentCache.domainConfig[domain];
    if (!domainConfig) {
        return undefined;
    }

    // Look up the environment config for the environment key
    const environmentConfig = cloudEnvironmentCache.environmentConfig[domainConfig.environmentKey];
    if (!environmentConfig) {
        return undefined;
    }

    return makeCloudEnvironment(domainConfig, environmentConfig);
}

function makeCloudEnvironment(
    domainConfig: DomainConfig,
    environmentConfig: EnvironmentConfig
): CloudEnvironment {
    // Merge the domain config and environment config to return a full CloudEnvironment object
    return {
        environmentKey: domainConfig.environmentKey,
        msaAuthenticationEndpoint: environmentConfig.msaAuthenticationEndpoint,
        orgAuthenticationEndpoint: environmentConfig.orgAuthenticationEndpoint,
        outlookWebOrigin: environmentConfig.outlookWebOrigin,
        tenantId: domainConfig.tenantId,
        usesGlobalDirectory: environmentConfig.usesGlobalDirectory,
    };
}

export const CLOUD_ENVIRONMENT_CACHE_KEY: LocalStorageKeys = 'CloudEnvironment.Cache';

/**
 * Gets the CloudEnvironmentCache from actual local storage, proxying the getItem invocation to the main thread context if we are in a worker context.
 */
async function getAuthoritativeCloudEnvironmentCache(): Promise<CloudEnvironmentCache> {
    const cloudEnvironmentCacheString = await authoritativeGetItem(CLOUD_ENVIRONMENT_CACHE_KEY);
    return parseCloudEnvironmentCacheString(cloudEnvironmentCacheString);
}

/**
 * Gets the CloudEnvironmentCache from the in-context local storage, e.g. if we're in the main thread context then
 * this hits actual local storage, but if we're in a worker context then this hits the worker's in-memory snapshot of local storage.
 */
function getInContextCloudEnvironmentCache(): CloudEnvironmentCache {
    const cloudEnvironmentCacheString = getItem(self, CLOUD_ENVIRONMENT_CACHE_KEY);
    return parseCloudEnvironmentCacheString(cloudEnvironmentCacheString);
}

/**
 * Attempts to parse a string as a CloudEnvironmentCache object, returning an empty cache object if parsing fails.
 */
function parseCloudEnvironmentCacheString(cacheString: string | null): CloudEnvironmentCache {
    if (cacheString) {
        const parsedCache = JSON.parse(cacheString);
        if (isCloudEnvironmentCache(parsedCache)) {
            return parsedCache;
        }
    }

    return { domainConfig: {}, environmentConfig: {} };
}

function setCloudEnvironmentCache(cloudEnvironmentCache: CloudEnvironmentCache): void {
    setItem(self, CLOUD_ENVIRONMENT_CACHE_KEY, JSON.stringify(cloudEnvironmentCache));
}
